import { PersonaDefinitionHistoryRow } from '@/common/api/params/persona'

import Text, { TextColor } from '@/components/view/atoms/text/Text'

/**
 * 定義の変更履歴。全体管理（カタログ）とサイト（実体）で共用する。
 *
 * **変更前後を並べて読めることが目的。** 監査ログは「誰が何の操作をしたか」の
 * 記録で、閾値がどう動いたかを追う用途には耐えないため、こちらを別に持っている。
 */

const FIELD_LABELS: Record<string, string> = {
  name: '表示名',
  judge_text: '判定条件（原文）',
  conditions: '条件式',
  tactics: '施策例',
  avatar_emoji: 'アイコン',
  avatar_color: '色',
  draft_note: '注記',
  priority: '優先度'
}

/** 条件式は式そのものを見せず、1行1条件の読める形にする */
const describeConditions = (v: unknown): string[] => {
  const all = (v as { all?: unknown } | null)?.all
  if (!Array.isArray(all)) return [JSON.stringify(v)]
  return all.map((node) => {
    const leaf = node as Record<string, unknown>
    if (typeof leaf?.metric !== 'string') return JSON.stringify(node)
    const src = leaf.src === 'pct' ? '（順位）' : ''
    return `${leaf.metric}${src} ${leaf.op} ${String(leaf.value)}`
  })
}

const describe = (field: string, v: unknown): string[] => {
  if (v === null || v === undefined) return ['（なし）']
  if (field === 'conditions') return describeConditions(v)
  if (Array.isArray(v)) return v.length ? v.map(String) : ['（なし）']
  return [String(v)]
}

const DefinitionHistory = ({ items }: { items: PersonaDefinitionHistoryRow[] }) => {
  if (!items.length) {
    return (
      <Text size={12} color={TextColor.SECONDARY}>
        まだ変更の記録がありません
      </Text>
    )
  }

  return (
    <div className='flex flex-col gap-3'>
      {items.map((h) => (
        <div key={h.id} className='border-border rounded-lg border p-3'>
          <div className='flex flex-wrap items-center gap-2'>
            <Text size={12} weight='bold'>
              {h.created_at ? new Date(h.created_at).toLocaleString('ja-JP') : '—'}
            </Text>
            <Text size={10} color={TextColor.SECONDARY}>
              {h.operator_name || '（不明）'}
              {h.version ? ` ・ 版 ${h.version}` : ''}
            </Text>
          </div>
          {h.note && (
            <div className='mt-1'>
              <Text size={10} color={TextColor.SECONDARY}>
                {h.note}
              </Text>
            </div>
          )}
          <div className='mt-2 flex flex-col gap-2'>
            {(h.changed_fields ?? []).map((f) => (
              <div key={f}>
                <Text size={10} weight='bold'>
                  {FIELD_LABELS[f] ?? f}
                </Text>
                <div className='mt-1 grid grid-cols-1 gap-1 sm:grid-cols-2'>
                  <div>
                    <Text size={10} color={TextColor.TERTIARY}>
                      変更前
                    </Text>
                    {describe(f, h.before?.[f]).map((line, i) => (
                      <div key={i}>
                        <Text size={10} color={TextColor.SECONDARY}>
                          {line}
                        </Text>
                      </div>
                    ))}
                  </div>
                  <div>
                    <Text size={10} color={TextColor.TERTIARY}>
                      変更後
                    </Text>
                    {describe(f, h.after?.[f]).map((line, i) => (
                      <div key={i}>
                        <Text size={10}>{line}</Text>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

export default DefinitionHistory
